import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, RefreshCw, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { get, post } from "@/lib/api";
import type { MailSyncStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import { importWarning, syncLabel } from "./mail";

/**
 * Firmen header: "Mails abgeglichen vor 12 Min." / "Abgleich fehlgeschlagen: …" / "HEY nicht verbunden" and "Jetzt abgleichen",
 * plus "Import unvollständig: …" below while an import stopped at HEY's page cap.
 */
export function MailSyncBar({ className }: { className?: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["mail-sync"],
    queryFn: () => get<MailSyncStatus>("/api/mail/sync"),
    refetchInterval: (query) => (query.state.data?.running ? 3000 : 60_000),
  });
  const start = useMutation({
    mutationFn: () => post<MailSyncStatus>("/api/mail/sync"),
    onSuccess: (s) => qc.setQueryData(["mail-sync"], s),
    onError: (e) => toast.error(e.message),
  });
  const running = q.data?.running ?? false;
  const wasRunning = useRef(running);
  useEffect(() => {
    // A run just finished: last contact, timelines and the start-page card may have changed.
    if (wasRunning.current && !running) {
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["company"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    }
    wasRunning.current = running;
  }, [running, qc]);
  // Re-render every minute so "vor 12 Min." stays true.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!q.data) return null;
  const label = syncLabel(q.data, now);
  const warning = importWarning(q.data);
  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <div className="flex min-w-0 flex-col">
        <span role="status" title={label.detail ?? label.text}
          className={cn("min-w-0 max-w-44 truncate text-sm sm:max-w-72", label.tone === "error" ? "text-destructive" : "text-muted-foreground")}>
          {label.tone === "error" && <AlertCircle aria-hidden className="mr-1 inline size-4 align-text-bottom" />}
          {label.text}
        </span>
        {warning && (
          <span title={warning} className="min-w-0 max-w-44 truncate text-xs text-muted-foreground sm:max-w-72">
            <TriangleAlert aria-hidden className="mr-1 inline size-3.5 align-text-bottom text-warning" />
            {warning}
          </span>
        )}
      </div>
      {q.data.configured && (
        <Button variant="outline" size="sm" aria-label="Jetzt abgleichen" disabled={running || start.isPending} onClick={() => start.mutate()} className="shrink-0">
          <RefreshCw className={cn(running && "animate-spin motion-reduce:animate-none")} />
          <span className="max-sm:hidden">Jetzt abgleichen</span>
        </Button>
      )}
    </div>
  );
}
